/**
 * One body's air: the scattering tables and sky quad Mars-class bodies mount,
 * the per-frame sky and aerial-term update the tick and cruise both call, and
 * the mount/unmount that keys them to whichever body's ground is drawn.
 */

import * as THREE from 'three';

import type { AtlasState } from '../atlas';
import type { BodyRow } from '../world/types';
import { deckAlbedo } from '../world/bodies.ts';
import { type OrreryBody, ORRERY_SUN_INTENSITY } from './orrery.ts';
import { createAtmosphere, type AtmosphereHandle } from './atmosphere.ts';
import { createAtmoSky, SKY_EXPOSURE, SKY_MIN_ANGULAR_DIAMETER_DEG, type AtmoSkyHandle } from './atmoSky.ts';
import { softClimate, solarFluxWm2, dustOpticalDepth, type PlotClimate } from './plotField.ts';
import type { TileMeshAtmoRef } from './tileMesh';
import type { PlotAtmoRef } from './dtmPlot';
import type { CoverageFillRef } from './coverageFill';
import type { BodyReadersHandle } from './bodyReaders.ts';
import type { BodySceneHandle } from './bodyScene.ts';
import type { CameraRigHandle } from './cameraRig.ts';
import type { DojoPaintsHandle } from './dojoPaints.ts';
import type { FocusRigHandle } from './focusRig.ts';
import { ATLAS_CLEAR_HEX, type MountHandle } from './mount.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { PlotSunHandle } from './plotSun.ts';
import type { SolarScrubHandle } from './solarScrub.ts';
import type { SurfaceRadiusHandle } from './surfaceRadius.ts';
import { seaDebug, seaUniforms, setSeaUniforms } from './seaSurface.ts';

export interface AtmosphereViewDeps {
  readonly renderer: THREE.WebGLRenderer;
  readonly mount: MountHandle;
  own(fn: () => void): void;
  readonly state: AtlasState;
  readonly bodyScene: BodySceneHandle;
  readonly bodyReaders: BodyReadersHandle;
  readonly dojoPaints: DojoPaintsHandle;
  readonly cameraRig: CameraRigHandle;
  readonly plotMount: PlotMountHandle;
  readonly plotSun: PlotSunHandle;
  readonly solarScrub: SolarScrubHandle;
  readonly surfaceRadius: SurfaceRadiusHandle;
  readonly focusRig: FocusRigHandle;
  readonly pyramidView: {
    applyBodyDrawOrder(): void;
    readonly occlusionDebug: { readonly parentAirDown: boolean };
  };
  readonly globeMaterial: {
    applyGlobeAtmo(material: THREE.MeshStandardMaterial, ref: TileMeshAtmoRef,
      coverage: CoverageFillRef | null): void;
    clearGlobeAtmo(material: THREE.MeshStandardMaterial, coverage: CoverageFillRef | null): void;
  };
  readonly shaderWarm: { warmDue: boolean; warmQuiet: number };
}

/** What `updateAtmoSky` last gated the sky quad on, for `descent-dojo --skyband --survey`. */
export interface AtmoSkyGate {
  /** Calls that reached the gate, which a missing table, quad or node skips. */
  evaluated: number;
  layerOn: boolean;
  climate: boolean;
  airDrawn: boolean;
  diameterDeg: number;
  /** Frames a table, sky-quad or aerial input read non-finite, with every upstream value on the first. */
  nonFinite: number;
  nonFiniteFirst: Record<string, unknown> | null;
}

export interface AtmosphereViewHandle {
  mountAtmo(row: BodyRow, onBodyId: string): void;
  unmountAtmo(): void;
  syncAtmoMount(): void;
  updateAtmoFrame(yieldPass: boolean): void;
  /** How much of the mounted air's own look is drawn, 0 to 1: `uAtmoLook` and the sky's fade. */
  setLook(look: number): void;
  tileAtmoRef(): TileMeshAtmoRef | null;
  plotAtmoRef(): PlotAtmoRef | null;
  readonly atmoTerrainUniforms: Record<string, THREE.IUniform>;
  readonly aerialDebug: {
    shellGround: boolean; legacySkyOrder: boolean; off: boolean;
    /** `globe-imagery-check --inject layer-haze`: the ground hazed with the ATMOSPHERE layer off. */
    legacyLayerHaze: boolean;
    /** `descent-dojo --arrival --inject table-sun`: `atmoSunMu` reads the sun the tables last took. */
    tableSun: boolean;
    /** `--survey-nan`: the next sky frame reads a NaN up and sun, as a dev frame did. */
    nanSkyFrame: boolean;
    /** `--inject sky-latch`: a refused frame reaches the sky quad and the ground tint again. */
    nonFiniteViewGuardOff: boolean;
  };
  /**
   * The sun's local-zenith component over the mounted air's body, this frame.
   *
   * For light set before `updateAtmoFrame` runs: `uAtmoSunDir` is last tick's,
   * and on a mount's first tick it is still `createAtmosphere`'s sun overhead.
   */
  atmoSunMu(): number;
  readonly atmo: AtmosphereHandle | null;
  readonly atmoSky: AtmoSkyHandle | null;
  atmoLayerOn: boolean;
  readonly atmoBodyId: string | null;
  readonly atmoGlobeMat: THREE.MeshStandardMaterial | null;
  readonly atmoGroundM: number;
  atmoDeckMCache: readonly [number, number] | null | undefined;
  readonly skySunAltitudeDeg: number | null;
  readonly skyGate: Readonly<AtmoSkyGate>;
}

export function createAtmosphereView(deps: AtmosphereViewDeps): AtmosphereViewHandle {
  const { renderer, own, state } = deps;
  const { atlasRoot, camera, cameraWorld } = deps.mount;

  /**
   * Mars's scattering LUTs.
   *
   * Built when the registry lands so the tables exist before a descent needs
   * them, and amortised one pass a frame from the tick. `atmoSky` draws the sky
   * and the limb from the sky-view table; `atmoTerrainUniforms` is the same
   * chunk's aerial term, read by the tiles at CLAIM/PLOT and by the plot mesh
   * at SURFACE.
   */
  let atmo: AtmosphereHandle | null = null;
  let atmoSky: AtmoSkyHandle | null = null;
  /** The ATMOSPHERE layer row's own switch, read inside `updateAtmoSky`. */
  let atmoLayerOn = true;
  /**
   * Which body's LUTs `atmo` holds, and whose globe and sky they are drawn on.
   *
   * The focused body for its own air; its PRIMARY while a moon of it is focused
   * (`parentAirId`); the ladder body under `--inject-sky`.
   */
  let atmoBodyId: string | null = null;
  /** The globe material the mount installed the aerial chunk on. Not always the
   *  focused globe, so a second globe cannot pick the chunk up by focus alone
   *  and the teardown gives it back to the one that has it. */
  let atmoGlobeMat: THREE.MeshStandardMaterial | null = null;
  /** The season, solar constant and dust curve the mounted air is driven from:
   *  `atmoBodyId`'s own, which is not the focused body's while a moon is. */
  let atmoClimate: PlotClimate | null = null;
  /** Everything one `mountAtmo` created, so `--inject-sky` can take it back. */
  let atmoTeardown: (() => void) | null = null;
  /** The body's own radius, taken once at mount so the tick allocates nothing. */
  let atmoGroundM = 0;
  /** `descent-dojo --underlay --inject shell-ground`: the focused globe's march
   *  back on the scattering shell's ground radius instead of the datum it is
   *  drawn on. Never the game. */
  /** `--airorder --inject legacy-sky` rides here too: the shell drawn first and
   *  out of the depth test, with every globe back on one order. */
  const aerialDebug = {
    shellGround: false, legacySkyOrder: false, off: false, legacyLayerHaze: false, tableSun: false,
    nanSkyFrame: false, nonFiniteViewGuardOff: false,
  };
  const _atmoUp = new THREE.Vector3();
  const _atmoToSun = new THREE.Vector3();
  const _atmoSunLocal = new THREE.Vector3();

  /**
   * The aerial-perspective term's own uniforms, beyond `atmo.uniforms`.
   *
   * Shared by reference with every tile material (`TileMeshAtmoRef`) and with
   * the plot (`PlotAtmoRef`), so this is the ONE place that sets them: a tile
   * mounted this frame and a tile mounted a hundred frames ago read the same
   * objects, and the plot reads the same `uAtmoSunIrradiance` /
   * `uAtmoAerialOff` the tiles do, which is what keeps the skyline from
   * stepping between them.
   */
  const atmoTerrainUniforms: Record<string, THREE.IUniform> = {
    uAtmoFrame: { value: new THREE.Matrix3() },
    uAtmoTerrainSunDir: { value: new THREE.Vector3(0, 1, 0) },
    uAtmoSunIrradiance: { value: 0 },
    /**
     * What the ground's own colour is multiplied by before the haze is added
     * to it: the physical irradiance over the orrery point light's intensity.
     *
     * three's Lambert term puts the globe and the tiles on
     * `ORRERY_SUN_INTENSITY * mu * albedo / pi`, a look that lights sixteen
     * bodies four decades apart, while the in-scatter beside it carries
     * `solarFluxWm2 * SKY_EXPOSURE`. Summing the two at 0.55x left 79 percent
     * of Mars's disc at ORBIT reading as atmosphere with the albedo map barely
     * visible under it, which is what the pale disc was. This is the
     * ORBIT-versus-LOCAL exposure gap docs/roadmap/05-atmosphere-and-light.md
     * names under "Sun and exposure", closed for the focused body's ground:
     * the tone curve then carries the frame, as it already does for the sky.
     * It scales the orrery's fake ambient fill with the direct term, which is
     * the night side, and only the consumers that carry the aerial chunk - the
     * focused body's globe and its tiles - read it at all.
     */
    uAtmoGroundScale: { value: 1 },
    /**
     * How much of the aerial chunk's answer the globe draws over the orrery's
     * own lit colour, 0 to 1: cruise eases a body's own look in on approach
     * (`worldLook` in src/cruise/systems.ts); every Atlas view holds it at 1.
     */
    uAtmoLook: { value: 1 },
    /**
     * The deck's own diffuse radiance, `syncGroundSky`'s `_skyLightT`: the same
     * dome the sky shader draws under an opaque deck, in the aerial in-scatter's
     * own units. Added to the far ground and to the haze in front of it so both
     * converge on the dome's light as the air thickens, rather than on black.
     * Zero on every body with no deck, and under `--inject unlit-far`.
     */
    uAtmoDeckSkyLight: { value: new THREE.Vector3(0, 0, 0) },
    uAtmoAerialOff: { value: 0 },
    /**
     * `descent-dojo --inject aerial-tail-on-ground`: put the sky-convergence
     * tail's sphere gate back on a terrain fragment instead of the fixed
     * `endsOnGround` flag, reproducing the mid-frame haze band over ground.
     */
    uAtmoAerialTailOnGround: { value: 0 },
    /**
     * `level-edge-check --inject skirt-haze`: haze a tile's skirt as if it
     * stood 200 km off, which draws a level-boundary crack as a light line.
     */
    uAtmoSkirtHaze: { value: 0 },
    /**
     * The cloud top's own reflectance, and whether the focused body's globe is
     * drawing it in place of its map.
     *
     * Above an opaque deck the globe IS the deck: Venus's map is Magellan radar
     * backscatter, which no eye outside the deck has ever seen, and drawing it
     * there is the same lie as drawing the surface. One is set while the eye is
     * over the deck's TOP, which is also where the LUT's ground is the deck, so
     * the two switch on the same frame and the crossing has no step in it.
     */
    uAtmoDeckAlbedo: { value: new THREE.Vector3(1, 1, 1) },
    uAtmoDeckDraw: { value: 0 },
    /**
     * `descent-dojo --haze --inject quilt`: put the globe's march back on the
     * tessellated mesh's own distance instead of the analytic ray-ground one,
     * which is the artifact the `quilt` criterion reads. Only the globe's
     * chunk declares it - a tile's distance IS its mesh.
     */
    uAtmoMeshDist: { value: 0 },
    /**
     * The radius the focused globe is DRAWN on, which is where its analytic
     * march has to stop: the pyramid's `datum_radius_m`, not the registry mean
     * radius the scattering shell is built on. 0 means "not known yet", and the
     * chunk falls back to the shell's own ground.
     *
     * `descent-dojo --underlay --inject shell-ground` writes 0 to put the
     * pre-fix march back.
     */
    uAtmoGlobeRadius: { value: 0 },
    /**
     * `body-sweep --inject night-fill`: put `uAtmoGroundScale` back on the
     * orrery's fake ambient fill as well as on the direct term.
     *
     * The scale converts the point light's own units into the physical ones
     * the in-scatter carries, so it belongs to the term the sun actually made.
     * The fill is not an irradiance and has nothing to convert, and scaling it
     * multiplies the light on an atmospheric body's UNLIT hemisphere by the
     * body's own flux over 320 W/m2 - 8.13 on Venus, 4.25 on Earth. 1 is the
     * old behaviour, which is what the night-side criterion's control reads.
     */
    uAtmoFillScale: { value: 0 },
    ...seaUniforms(),
  };
  const _atmoFrameT1 = new THREE.Vector3();
  const _atmoFrameT2 = new THREE.Vector3();
  const _atmoAxisX = new THREE.Vector3(1, 0, 0);
  const _atmoAxisY = new THREE.Vector3(0, 1, 0);

  /**
   * The sun the LUTs are built from: the orrery's own geometry.
   *
   * The body's position against the camera's radial - the same geometry
   * `syncSolar` hands the plot's `sunAngles`, so there is one sun now and this
   * is where it comes from.
   *
   * The tables are azimuthally symmetric about the zenith, so the sun's azimuth
   * folds onto +x here and only its altitude reaches the model; what a consumer
   * needs is the view's azimuth RELATIVE to the sun's, which is what the
   * sky-view table's second axis carries.
   */
  /**
   * The orrery node the mounted air is about.
   *
   * The focused body's own globe for its own air, and the primary's while a
   * moon of it is focused: every uniform the shell and the aerial term read is
   * a radius, a zenith or a sun angle measured from THAT body's centre.
   */
  function atmoNode(): OrreryBody | null {
    if (atmoBodyId === null) return null;
    if (deps.bodyScene.state.bodyNode && deps.bodyScene.state.bodyNode.row.id === atmoBodyId) return deps.bodyScene.state.bodyNode;
    return deps.bodyScene.state.orrery?.byId(atmoBodyId) ?? null;
  }

  function atmoSunDirLocal(): THREE.Vector3 | null {
    const node = atmoNode();
    if (!node) return null;
    _atmoUp.set(
      cameraWorld.x - node.world.x, cameraWorld.y - node.world.y, cameraWorld.z - node.world.z);
    _atmoToSun.set(-node.world.x, -node.world.y, -node.world.z);
    if (_atmoUp.lengthSq() === 0 || _atmoToSun.lengthSq() === 0) return null;
    const mu = THREE.MathUtils.clamp(
      _atmoUp.normalize().dot(_atmoToSun.normalize()), -1, 1);
    _atmoSunLocal.set(Math.sqrt(Math.max(0, 1 - mu * mu)), mu, 0);
    return _atmoSunLocal;
  }

  function atmoSunMu(): number {
    if (aerialDebug.tableSun) return atmo ? (atmo.uniforms['uAtmoSunDir']!.value as THREE.Vector3).y : 1;
    return atmoSunDirLocal()?.y ?? 1;
  }

  const _skyUp = new THREE.Vector3();
  const _skySun = new THREE.Vector3();
  /**
   * The sun's altitude in the frame the SKY was last drawn with, degrees.
   *
   * Not `sunAltitudeDeg` and not `plotSun.altitudeDeg`: which of those two the
   * sky is a function of depends on the regime, so a reader that picked one
   * labelled a lit sky night on a body where they disagree. Null while nothing
   * is drawn.
   */
  let skySunAltitudeDeg: number | null = null;
  const skyGate: AtmoSkyGate = {
    evaluated: 0, layerOn: true, climate: false, airDrawn: false, diameterDeg: 0, nonFinite: 0, nonFiniteFirst: null,
  };
  let skyRefused = false;
  const fin3 = (v: { x: number; y: number; z: number }): boolean =>
    Number.isFinite(v.x) && Number.isFinite(v.y) && Number.isFinite(v.z);
  function noteNonFinite(where: string, extra: Record<string, unknown>): void {
    skyGate.nonFinite++;
    if (skyGate.nonFiniteFirst !== null) return;
    const node = atmoNode();
    const pw = deps.plotMount.plotWorld;
    let groundUnderEyeM: number | null = null;
    if (node) {
      const dx = cameraWorld.x - node.world.x;
      const dy = cameraWorld.y - node.world.y;
      const dz = cameraWorld.z - node.world.z;
      groundUnderEyeM = deps.surfaceRadius.cruiseGroundUnderEyeM(node, dx, dy, dz, Math.hypot(dx, dy, dz));
    }
    skyGate.nonFiniteFirst = {
      where, gateEvaluated: skyGate.evaluated, ...extra,
      cameraWorld: [cameraWorld.x, cameraWorld.y, cameraWorld.z],
      nodeWorld: node ? [node.world.x, node.world.y, node.world.z] : null,
      plotCamera: deps.cameraRig.plotCamera(),
      camPos: deps.cameraRig.camPos.toArray(),
      plotWorld: [pw.x, pw.y, pw.z],
      plotQuat: deps.plotMount.plotQuat.toArray(),
      plotUp: deps.plotMount.plotUpWorld.toArray(),
      plotNodeQuat: node ? node.quaternion.toArray() : null,
      groundUnderEyeM,
      plotSun: deps.plotSun.plotSunDirLocal.toArray(),
      lsDeg: deps.solarScrub.solar.lsDeg,
      sunScale: deps.plotSun.state.sunScale,
    };
    console.warn('atmosphere: first non-finite input', JSON.stringify(skyGate.nonFiniteFirst));
  }

  /**
   * The camera's altitude over the ground under it, metres.
   *
   * NOT `altitude()`, which is the focus camera's SLANT distance to the ground
   * point it looks at: at CLAIM's 5 km and 8 degrees of pitch the eye is 0.70 km
   * up, and an ORBIT camera 6,000 km from its anchor at pitch 0 is 3,500 km out.
   * The scattering tables are indexed by RADIUS and the sky's ray starts at one,
   * so feeding the slant put the limb's own silhouette thousands of kilometres
   * off - drawn inside the disc, where the planet covers it.
   */
  function atmoAltitudeM(): number {
    const node = atmoNode();
    if (!node) return Math.max(deps.focusRig.altitude(), 0);
    const dx = cameraWorld.x - node.world.x;
    const dy = cameraWorld.y - node.world.y;
    const dz = cameraWorld.z - node.world.z;
    const r = Math.hypot(dx, dy, dz);
    return Math.max(r - deps.surfaceRadius.cruiseGroundUnderEyeM(node, dx, dy, dz, r), 0);
  }

  /**
   * The flux the whole atmospheric path is exposed at, W/m2.
   *
   * The sky, the in-scatter in front of the ground and `uAtmoGroundScale` -
   * the conversion that puts the ground's own Lambert term in the same units
   * as that in-scatter - are all driven from this one number, so a floor on it
   * moves the three together and leaves every ratio between them alone. It is
   * the CAMERA's exposure and never the atmosphere's physics.
   *
   * The floor is the orrery's own point light, which lights sixteen bodies
   * four decades of flux apart at one look. Below it the focused body is drawn
   * dimmer than the same ground would be with no atmosphere at all, which is
   * the fault Pluto was: 0.873 W/m2 put `uAtmoGroundScale` at 0.0025, so its
   * disc read 0.0 per cent lit and a mean of 0.0 against airless Charon's 86.7
   * per cent and 104.6 beside it, while Pluto's own 1 Pa column takes out
   * 2.6 per cent of the light (transmittance table mean 0.974). At the floor a
   * column that scatters nothing reduces exactly to the airless look, which is
   * the limit any near-vacuum row has to reach - Triton, Io and Enceladus are
   * the same class. Mars, Earth and Venus sit at 493-707, 1361 and 2601 W/m2
   * and never reach it. The floor follows the light at its `sunScale`, so it
   * stays the airless look when that look carries the season.
   */
  const ATMO_DRIVE_FLOOR_WM2 = ORRERY_SUN_INTENSITY / SKY_EXPOSURE;
  const atmoDriveFluxWm2 = (climate: PlotClimate): number =>
    Math.max(solarFluxWm2(deps.solarScrub.solar.lsDeg, climate) * deps.plotSun.cover.skyGround,
      ATMO_DRIVE_FLOOR_WM2 * deps.plotSun.state.sunScale);

  /**
   * Point the sky at the frame about to be drawn.
   *
   * Two frames, and the sky takes whichever one the CAMERA is in, because the
   * view ray it unprojects is in that frame and nothing else: above the claim
   * the camera sits at the scene origin with the world frame around it, and
   * inside the claim the plot owns a local y-up frame with no globe in it
   * (`surfaceRegime`, and the `camera.up` note beside it). What crosses the edge
   * unchanged is the two numbers the sky is actually a function of - the sun's
   * altitude and the camera's - because both frames now read them off the same
   * ephemeris.
   *
   * The radius is `uAtmoViewHeight`'s own, deliberately: the sky-view table was
   * built at that height, so a sky evaluated at any other one is reading a
   * table for somewhere else.
   */
  function updateAtmoSky(): void {
    const sky = atmoSky;
    const node = atmoNode();
    skyRefused = false;
    if (!sky || !atmo || !node) return;
    const rTopM = (atmo.uniforms['uAtmoRadii']!.value as THREE.Vector2).y;
    const dM = atmoGroundM + atmoAltitudeM();
    // A shell drawn under a pixel wide is not a sky, so switching it off there
    // cannot be seen, let alone read as a pop.
    const halfDeg = dM > rTopM ? (Math.asin(rTopM / dM) * 180) / Math.PI : 90;
    const climate = atmoClimate;
    const drawn = airDrawn();
    skyGate.evaluated++;
    skyGate.layerOn = atmoLayerOn;
    skyGate.climate = climate !== null;
    skyGate.airDrawn = drawn;
    skyGate.diameterDeg = halfDeg * 2;
    if (!atmoLayerOn || !climate || !drawn
      || halfDeg * 2 < SKY_MIN_ANGULAR_DIAMETER_DEG) {
      sky.mesh.visible = false;
      skySunAltitudeDeg = null;
      return;
    }
    if (deps.cameraRig.plotCamera()) {
      // Only the focused body's own air reaches here: `parentAirId` refuses a
      // primary's inside a moon's walkaround frame, where this y-up basis is
      // the moon's and the primary is nowhere in it. Both are DIRECTIONS in the
      // plot's own frame and the ray below is unprojected in the drawn one, so
      // they are turned by the same basis the plot is registered with.
      _skyUp.copy(deps.plotMount.plotUpWorld);
      _skySun.copy(deps.plotSun.plotSunDirLocal).applyQuaternion(deps.plotMount.plotQuat);
    } else {
      _skyUp.set(
        cameraWorld.x - node.world.x, cameraWorld.y - node.world.y, cameraWorld.z - node.world.z);
      // Sol IS the frame origin, so the direction to it is the camera's own
      // heliocentric position negated. The parallax across the body - 3,400 km
      // against 2e8 - is under a thousandth of a degree.
      _skySun.set(-cameraWorld.x, -cameraWorld.y, -cameraWorld.z);
      if (_skyUp.lengthSq() === 0 || _skySun.lengthSq() === 0) {
        sky.mesh.visible = false;
        skySunAltitudeDeg = null;
        return;
      }
      // Both are heliocentric-scale (order 1e11 m) here, not unit vectors -
      // `atmoSky.update` normalizes its OWN copy of each, but this module's
      // copies stay raw unless normalized here, which is what
      // `updateAtmoTerrainFrame` below needs them to be.
      _skyUp.normalize();
      _skySun.normalize();
    }
    // The ray is unprojected from the camera's own basis, which `lookAt` and
    // `focusCam.apply` leave on the quaternion without touching the matrix.
    camera.updateMatrixWorld();
    if (aerialDebug.nanSkyFrame) {
      aerialDebug.nanSkyFrame = false;
      _skySun.x = Number.NaN;
      _skyUp.x = Number.NaN;
    }
    const sunAltDeg = (Math.asin(
      THREE.MathUtils.clamp(_skyUp.dot(_skySun), -1, 1)) * 180) / Math.PI;
    const flux = atmoDriveFluxWm2(climate);
    if (!Number.isFinite(dM) || !fin3(_skyUp) || !fin3(_skySun) || !Number.isFinite(flux)
      || !fin3(camera.position) || !fin3(camera.quaternion)) {
      noteNonFinite('sky', { dM, skyUp: _skyUp.toArray(), skySun: _skySun.toArray(), flux,
        camera: camera.position.toArray(), cameraQ: camera.quaternion.toArray() });
      // The quad and the ground tint keep their last good uniforms this frame.
      skyRefused = !aerialDebug.nonFiniteViewGuardOff;
      if (skyRefused) return;
    }
    skyRefused = false;
    skySunAltitudeDeg = sunAltDeg;
    sky.update(camera, _skyUp, dM, _skySun, flux);
    sky.setFade(atmoTerrainUniforms['uAtmoLook']!.value as number);
    sky.setHueKeep(!deps.cameraRig.lookUpDebug.whiteGlow);
    sky.mesh.visible = true;
  }

  const _atmoBasis = new THREE.Matrix3();
  const _atmoCamRot = new THREE.Matrix3();
  // `params().deckM` is fixed for a mount - the only thing that ever moves it
  // is the `deckOff` debug flag - but `params()` reallocates an object plus
  // three arrays to hand back the one field this reads. `undefined` means
  // "recompute": set on every mount and whenever `deckOff` is armed.
  let atmoDeckMCache: readonly [number, number] | null | undefined;

  /**
   * The frame the aerial-perspective term on the tiles and the plot reads:
   * the SAME `_skyUp`/`_skySun` `updateAtmoSky` just set, decomposed into a
   * local-horizon basis - which is why this runs right after it in the tick
   * and gates on the SAME `atmoSky.mesh.visible` that call just decided,
   * rather than re-deriving when `_skyUp`/`_skySun` are valid.
   *
   * Only the basis differs from the sky quad's own per-fragment one (any
   * basis sharing the same `up` gives the same answer, since every
   * `atmosphere.ts` function is invariant to a rotation about it - which is
   * also why the plot mesh, whose own local frame is already y-up, needs no
   * matrix at all and reads `uSunDir` and `-V` straight through). This is a
   * per-frame uniform rather than a per-fragment one because it is the same
   * for every tile drawn this frame: building it once here is the six cross
   * products the sky quad already pays for the whole screen, not that cost
   * again per terrain fragment.
   *
   * `uAtmoFrame` carries the camera's own rotation folded in, because the
   * tile fragment reads it against `vViewPosition` - camera space, the one
   * every standard-material fragment already carries - rather than against a
   * world-space varying it would otherwise have to add.
   */
  function updateAtmoTerrainFrame(): void {
    // The globe's deck flag has to hold for the focused body even when the sky
    // quad is not drawn - a deck body seen too small to cross
    // SKY_MIN_ANGULAR_DIAMETER_DEG still needs its own deck read, not the last
    // body's - so this runs ahead of the aerial-frame gate below.
    if (atmo) {
      if (atmoDeckMCache === undefined) atmoDeckMCache = atmo.params().deckM;
      const deck = atmoDeckMCache;
      const over = deck !== null && atmoAltitudeM() >= deck[1];
      atmoTerrainUniforms['uAtmoDeckDraw']!.value = over ? 1 : 0;
      if (over) {
        const row = deps.bodyReaders.rowById(atmoBodyId);
        const air = row && row.atmosphere.kind === 'data' ? row.atmosphere : null;
        if (air) {
          const a = deckAlbedo(air);
          (atmoTerrainUniforms['uAtmoDeckAlbedo']!.value as THREE.Vector3).set(a[0], a[1], a[2]);
        }
      }
    }
    // Ahead of the same gate, for the same reason: the globe carries the chunk
    // wherever it is drawn, and a stale radius here is a march that stops in
    // the wrong place rather than a term that is merely off.
    atmoTerrainUniforms['uAtmoGlobeRadius']!.value = aerialDebug.shellGround
      ? 0 : (atmoNode()?.drawRadiusM ?? 0);
    // With the layer off the sky gate below never refreshes the frame, and the
    // ground went on hazed through a stale one: Titan's tau-7 column hid its map.
    const airless = !atmoLayerOn && !aerialDebug.legacyLayerHaze;
    atmoTerrainUniforms['uAtmoAerialOff']!.value = airless || aerialDebug.off ? 1 : 0;
    if (airless) atmoTerrainUniforms['uAtmoGroundScale']!.value = 1;
    atmoTerrainUniforms['uSeaSkyOn']!.value = airless || seaDebug.noSky ? 0 : 1;
    if (!atmo || !atmoSky || !atmoSky.mesh.visible || airless || skyRefused) return;
    const climate = atmoClimate;
    if (!climate) return;
    const t = atmoTerrainUniforms;
    const seed = Math.abs(_skyUp.y) > 0.9 ? _atmoAxisX : _atmoAxisY;
    _atmoFrameT1.crossVectors(_skyUp, seed).normalize();
    _atmoFrameT2.crossVectors(_skyUp, _atmoFrameT1);
    _atmoBasis.set(
      _atmoFrameT1.x, _atmoFrameT1.y, _atmoFrameT1.z,
      _skyUp.x, _skyUp.y, _skyUp.z,
      _atmoFrameT2.x, _atmoFrameT2.y, _atmoFrameT2.z,
    );
    camera.updateMatrixWorld();
    _atmoCamRot.setFromMatrix4(camera.matrixWorld);
    (t['uAtmoFrame']!.value as THREE.Matrix3).multiplyMatrices(_atmoBasis, _atmoCamRot);
    (t['uAtmoTerrainSunDir']!.value as THREE.Vector3).set(
      _skySun.dot(_atmoFrameT1), _skySun.dot(_skyUp), _skySun.dot(_atmoFrameT2),
    );
    const irradiance = atmoDriveFluxWm2(climate) * SKY_EXPOSURE;
    t['uAtmoSunIrradiance']!.value = irradiance;
    // Over the light as drawn: the rig already carries the season, and this
    // irradiance carries it too.
    t['uAtmoGroundScale']!.value = irradiance / (ORRERY_SUN_INTENSITY * deps.plotSun.state.sunScale);
    const tsun = t['uAtmoTerrainSunDir']!.value as THREE.Vector3;
    if (!fin3(tsun) || !Number.isFinite(t['uAtmoGroundScale']!.value as number) || !Number.isFinite(irradiance)
      || !(t['uAtmoFrame']!.value as THREE.Matrix3).elements.every(Number.isFinite)) {
      noteNonFinite('terrain', { terrainSun: tsun.toArray(), irradiance,
        groundScale: t['uAtmoGroundScale']!.value, frame: (t['uAtmoFrame']!.value as THREE.Matrix3).elements.slice() });
    }
  }

  /**
   * One frame of the mounted body's air: the scattering pass, the sky quad and
   * the aerial frame the globe and the tiles read.
   *
   * The tick calls it below the orrery, and cruise calls it from the world
   * update - the sky is a function of where the camera is and which body is
   * mounted, and in cruise neither of those is the ladder's.
   */
  function updateAtmoFrame(yieldPass: boolean): void {
    // The navy is only there to be blended under a sky; a body with no air sees black.
    const row = state.bodyId === null ? null : deps.bodyReaders.rowById(state.bodyId);
    deps.mount.space.setHex(row && row.atmosphere.kind !== 'data' ? 0x000000 : ATLAS_CLEAR_HEX);
    if (!atmo) return;
    const atmoSun = atmoSunDirLocal();
    if (atmoSun) {
      const climate = atmoClimate;
      const altM = atmoAltitudeM();
      const tau = climate ? dustOpticalDepth(deps.solarScrub.solar.lsDeg, climate.dust) : null;
      if (!Number.isFinite(altM) || !fin3(atmoSun) || (tau !== null && !Number.isFinite(tau))) {
        noteNonFinite('table', { altM, sun: [atmoSun.x, atmoSun.y, atmoSun.z], tau });
      }
      // A body with no dust curve passes null, which leaves the LUTs on the
      // registry's own constant column depth instead of a Martian season.
      atmo.update(atmoSun, altM, tau, yieldPass);
    }
    updateAtmoSky();
    updateAtmoTerrainFrame();
  }

  /**
   * Build a body's scattering tables, its sky quad and the warm programs.
   *
   * `onBodyId` is which body the sky is then DRAWN on, which is `row.id` for
   * every real mount. They come apart only for `--inject-sky`, whose whole
   * point is a sky built from one body's air and drawn on another's ground.
   */
  function mountAtmo(row: BodyRow, onBodyId: string): void {
    if (atmo) return;
    atmoBodyId = onBodyId;
    setSeaUniforms(atmoTerrainUniforms, row.ocean);
    const a = createAtmosphere({ renderer, row, greyGroundAlbedo: deps.surfaceRadius.GREY_GROUND_BOUNCE });
    atmo = a;
    atmoDeckMCache = undefined;
    // Cleared with the cache: a fresh mount reads its own deck on the next
    // frame, not the last body's left drawing on this one's globe meanwhile.
    atmoTerrainUniforms['uAtmoDeckDraw']!.value = 0;
    // The BODY's datum, not `params().rGroundM`: a deck body's LUT ground is
    // the cloud top and moves with the regime, and the camera's altitude has to
    // stay a fact about the body.
    atmoGroundM = row.radius_m;
    // The body the sky is DRAWN on, not the row the tables came from: under
    // `--inject-sky` the two are different bodies on purpose, and the season
    // and dust the frame is exposed for are the ground's own.
    atmoClimate = softClimate(deps.bodyReaders.rowById(onBodyId) ?? row, deps.bodyReaders.rowById).climate;
    const sky = createAtmoSky({ atmo: a });
    atmoSky = sky;
    atlasRoot.add(sky.mesh);
    // The order the shell needs around it is a fact about which body's air this
    // is, so it is settled here rather than on every tick.
    deps.pyramidView.applyBodyDrawOrder();
    // Same chunk, same frame uniforms, on the globe underneath the
    // pyramid - see `applyGlobeAtmo`.
    const globeNode = atmoNode();
    const globeMat = globeNode
      ? globeNode.mesh.material as THREE.MeshStandardMaterial
      : null;
    const globeCoverage = globeNode?.coverage ?? null;
    if (globeMat) {
      atmoGlobeMat = globeMat;
      deps.globeMaterial.applyGlobeAtmo(globeMat, { atmo: a, frameUniforms: atmoTerrainUniforms },
        globeCoverage);
    }
    // The sky quad's program, this body's tile programs and the plot's own are
    // all linked by `warmDescentPrograms` on the next frame, from inside the
    // draw bracket: the renderer state `stage.begin()` sets keys a program too,
    // so a compile from out here links one the draw then throws away.
    deps.shaderWarm.warmDue = true;
    deps.shaderWarm.warmQuiet = 0;
    atmoTeardown = (): void => {
      // The globe's chunk closes over THESE tables, and the orrery goes on
      // drawing that body after a focus change frees them.
      atmoGlobeMat = null;
      if (globeMat) deps.globeMaterial.clearGlobeAtmo(globeMat, globeCoverage);
      atlasRoot.remove(sky.mesh);
      sky.dispose();
      a.dispose();
      atmoSky = null;
      atmo = null;
      atmoBodyId = null;
      atmoClimate = null;
      deps.pyramidView.applyBodyDrawOrder();
    };
  }

  function unmountAtmo(): void {
    setSeaUniforms(atmoTerrainUniforms, null);
    atmoTeardown?.();
    atmoTeardown = null;
  }

  /**
   * The body whose air is drawn while ANOTHER body has the focus: the focused
   * body's primary, or null.
   *
   * Mars seen from Phobos or Deimos is the case this exists for. The air used
   * to be the focused body's alone, so a moon's focus drew its primary as a
   * bare disc - no limb glow and no haze over its ground - where the same
   * primary focused from the same distance draws both.
   *
   * The primary and not every body with air in frame: the tables are per body
   * and a second set is a second precompute, and the primary is the only body a
   * moon's own focus ever puts across the frame. Mars is 42 degrees wide from
   * Phobos and 17 from Deimos; every other body with air is under a tenth of a
   * degree from either.
   *
   * Null inside the moon's own plot frame: the sky quad unprojects its view ray
   * in whatever frame the camera is posed in, and a walkaround's is the moon's
   * own y-up basis with the primary nowhere in it, so the shell would be placed
   * under the ground the person is standing on.
   *
   * Null while `occlusionDebug.parentAirDown` is set: the occlusion check keys
   * the primary's own globe by an unlit colour, and the ground-scale term the
   * aerial chunk installs on that globe runs whether or not the aerial term
   * itself is lit, moving the keyed pixel out of the reader's threshold.
   */
  function parentAirId(): string | null {
    if (deps.dojoPaints.skyDebug.injectFrom !== null || state.bodyId === null || deps.cameraRig.plotCamera()
      || deps.pyramidView.occlusionDebug.parentAirDown) return null;
    const row = deps.bodyReaders.rowById(state.bodyId);
    if (!row || row.atmosphere.kind === 'data') return null;
    const parent = deps.bodyReaders.rowById(row.parent);
    return parent && parent.atmosphere.kind === 'data' ? parent.id : null;
  }

  /** Is the mounted air about a body this frame is of: the focused body's own,
   *  or its primary's in the background. */
  function airDrawn(): boolean {
    return atmoBodyId !== null
      && (atmoBodyId === state.bodyId || atmoBodyId === parentAirId());
  }

  /**
   * The air this frame needs, mounted, and nobody else's.
   *
   * Called from `syncBody` and once a tick, so a focus change takes the last
   * body's tables down instead of handing them to the next body's tiles and
   * plot: `atmoGroundM`, the LUTs and the aerial term are all one body's, and
   * the tile chunk reads them over whatever ground is on screen.
   */
  function syncAtmoMount(): void {
    // Before the orrery lands there is no globe to warm the chunk on, and the
    // registry's own `syncBody` is one round trip away.
    if (!deps.bodyScene.state.bodyNode) return;
    // `--inject-sky` mounts one body's air over another's ground on purpose.
    if (deps.dojoPaints.skyDebug.injectFrom !== null) return;
    const own = deps.bodyReaders.rowById(deps.bodyReaders.ladderBody());
    const id = own && own.atmosphere.kind === 'data' ? own.id : parentAirId();
    if (atmo && atmoBodyId === id) return;
    unmountAtmo();
    const row = deps.bodyReaders.rowById(id);
    if (row) mountAtmo(row, row.id);
  }

  own(() => unmountAtmo());

  /**
   * The scattering chunk, for a tile material. Null for a body with no
   * atmosphere, or before the registry has built one.
   *
   * `atmoBodyId` and not just `atmo`: the tables, the ground radius and the
   * aerial term belong to ONE body, and a tile carrying them over another
   * body's ground multiplies it by a Martian transmittance at altitude zero.
   * The sky's own gate (`updateAtmoSky`) reads the same pair.
   */
  function tileAtmoRef(): TileMeshAtmoRef | null {
    return atmo && atmoBodyId === state.bodyId
      ? { atmo, frameUniforms: atmoTerrainUniforms } : null;
  }

  /** The same chunk, on the same gate, shaped for the plot. */
  function plotAtmoRef(): PlotAtmoRef | null {
    return atmo && atmoBodyId === state.bodyId
      ? { atmo, sharedUniforms: atmoTerrainUniforms } : null;
  }

  function setLook(look: number): void {
    atmoTerrainUniforms['uAtmoLook']!.value = Number.isFinite(look) ? Math.min(1, Math.max(0, look)) : 1;
  }

  return {
    mountAtmo,
    unmountAtmo,
    syncAtmoMount,
    updateAtmoFrame,
    setLook,
    tileAtmoRef,
    plotAtmoRef,
    atmoTerrainUniforms,
    aerialDebug,
    atmoSunMu,
    get atmo() { return atmo; },
    get atmoSky() { return atmoSky; },
    get atmoLayerOn() { return atmoLayerOn; },
    set atmoLayerOn(v: boolean) { atmoLayerOn = v; },
    get atmoBodyId() { return atmoBodyId; },
    get atmoGlobeMat() { return atmoGlobeMat; },
    get atmoGroundM() { return atmoGroundM; },
    get atmoDeckMCache() { return atmoDeckMCache; },
    set atmoDeckMCache(v: readonly [number, number] | null | undefined) { atmoDeckMCache = v; },
    get skySunAltitudeDeg() { return skySunAltitudeDeg; },
    skyGate,
  };
}
